import { ExamRegistration } from "../types/types";
import { PAYMENT_ENDPOINTS } from "../constants/api";
import { privateApiClient } from "../utils/axios.utils";

export interface PaymentPayload {
  exam_registration_ids: number[];
  payment_method: string;
  transaction_id: string;
}

export interface Payment {
  id: number;
  amount: number;
  amount_due: number;
  momo_fee: number;
  discount: boolean;
  payment_status: string;
  payment_method: string;
  transaction_id: string;
  payment_attempts: number;
  exam_registration_id: number;
  approved_by: number | null;
  created_at: string;
  updated_at: string;
  exam_registration?: ExamRegistration;
}

export interface PaymentResponse {
  data: Payment[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
  };
}

export interface AdminExamPayment {
  school_id: number;
  school_name: string;
  school_region: string;
  school_division: string;
  school_contact: string | null;
  total_amount: number;
  total_fees: number;
  total_amount_due: number;
  total_discount: number;
  total_payable?: number;
  payment_status: "pending" | "completed" | "rejected";
  payment_method: string | null;
  transaction_id: string | null;
  exam_session_id: number;
  exam_session_name: string;
  created_at: string | null;
  updated_at: string | null;
  payment_ids: number[];
  details?: (Payment & {
    exam_type?: string;
    discount_amount?: number;
    discount_percentage?: number;
  })[];
}

export interface AdminExamPaymentsResponse {
  data: {
    data: AdminExamPayment[];
    pagination: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
      from: number;
      to: number;
    };
  };
}

export interface SinglePaymentResponse {
  data: Payment;
}

/**
 * Payment Service
 * This service handles all API calls related to payments.
 */
export const PaymentService = {
  /**
   * Fetch all payments for the authenticated school
   */
  getAllPayments: async (
    page: number = 1,
    limit: number = 10,
    status?: string,
    search: string = ""
  ): Promise<PaymentResponse> => {
    const params: {
      page: number;
      limit: number;
      status?: string;
      search?: string;
    } = {
      page,
      limit,
    };
    if (status && status !== "all") {
      params.status = status;
    }
    if (search) {
      params.search = search;
    }
    const response = await privateApiClient.get(PAYMENT_ENDPOINTS.GET_ALL, {
      params,
    });

    return response.data;
  },

  /**
   * Fetch all exam payments for admin (aggregated by school)
   */
  getAdminExamPayments: async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    status?: string,
    method?: string
  ): Promise<AdminExamPaymentsResponse> => {
    const params: any = { page, limit, search };
    if (status && status !== "all") params.status = status;
    if (method && method !== "all") params.payment_method = method;

    const response = await privateApiClient.get(
      PAYMENT_ENDPOINTS.ADMIN_GET_EXAM_PAYMENTS,
      { params }
    );
    return response.data;
  },

  /**
   * Update payment status for multiple payments (admin only)
   */
  updateExamPaymentStatus: async (data: {
    payment_ids: number[];
    payment_status: string;
    remarks?: string;
  }): Promise<{ data: Payment[] }> => {
    const response = await privateApiClient.put(
      PAYMENT_ENDPOINTS.ADMIN_UPDATE_STATUS,
      data
    );
    return response.data;
  },

  /**
   * Fetch a single payment by ID
   */
  getPayment: async (id: number): Promise<SinglePaymentResponse> => {
    const response = await privateApiClient.get(
      PAYMENT_ENDPOINTS.GET_BY_ID(id)
    );
    return response.data;
  },

  /**
   * Create a new payment
   */
  createPayment: async (
    data: PaymentPayload
  ): Promise<SinglePaymentResponse> => {
    const response = await privateApiClient.post(
      PAYMENT_ENDPOINTS.CREATE,
      data
    );
    return response.data;
  },

  /**
   * Update a payment status
   */
  updatePayment: async (
    id: number,
    data: Partial<Payment>
  ): Promise<SinglePaymentResponse> => {
    const response = await privateApiClient.put(
      PAYMENT_ENDPOINTS.UPDATE(id),
      data
    );
    return response.data;
  },

  /**
   * Delete an exam payment (admin only)
   */
  deleteExamPayment: async (
    schoolId: number,
    examSessionId: number
  ): Promise<unknown> => {
    const response = await privateApiClient.delete(
      `${PAYMENT_ENDPOINTS.ADMIN_GET_EXAM_PAYMENTS}/${schoolId}/${examSessionId}`
    );
    return response.data;
  },
};
